<template>
  <v-progress-circular
    v-if="callingApi"
    size="200"
    width="15"
    class="d-block mx-auto"
    indeterminate
    color="primary"
  />
  <span v-else-if="computedPrefList.length === 0">
        {{ $t('conflict.noResolutionToDo') }}
  </span>
  <div v-else-if="computedPrefList.length > 0 && selectedPref">
    
   <option-block
      v-if="!validatedOption && selectedPref.id"
      :list-label="selectedPref.id"
      :options="[ selectedPref[wigoProperty] ]"
      :elements="selectedPref.scenario.elements"
      :prefConflicts="computedPrefList"
      @changePref="changeSelectedPref($event, 1)"
      :extend="selectedPreferencesCombinationExtendable"
      :enableResolvedCheckMark="true"
      :resolved="isAllConflictResolved(selectedPref)"
      :defineResolvedFunction="isAllConflictResolved"
      :lock="lock"
      :belief-mode="beliefMode"
      :asGroup="!!selectedPref.groupId"
    />
    
    <option-block
      v-if="!validatedOption && conflictSelectedPref.id"
      :list-label="conflictSelectedPref.id"
      :options="[ conflictSelectedPref[wigoProperty] ]"
      :elements="conflictSelectedPref.scenario.elements"
      :prefConflicts="computedConflictPrefList.filter(p => p.id !== conflictSelectedPref.id)"
      @changePref="changeSelectedPref($event, 2)"
      :extend="selectedPreferencesCombinationExtendable"
      :enableResolvedCheckMark="true"
      :resolved="conflictSelectedPref.isCombinationResolved"
      :lock="lock"
      :belief-mode="beliefMode"
      :asGroup="!!conflictSelectedPref.groupId"
    />

    <v-row 
      v-if="!selectedPreferencesCombinationExtendable"
      dense
      justify="start"
      class="elevation-3 px-3 mx-1 py-4 my-5 green lighten-4"
    >
      <v-icon color="green">mdi-check</v-icon>{{ $t('conflict.preferencesCombinationTreated') }}
    </v-row>
    
    <v-row
      dense
      justify="start"
      class="elevation-3 px-3 mx-1 py-4 my-5 grey lighten-2"
    >
      <v-col cols="12">
        <h3 class="pa-3">{{ this.addElementsLabel }}</h3>
      </v-col>
      <v-col cols="12">
        <input-scenario-elements
          :key="inputScenarioElementsKey"
          :refresh="refreshInputScenarioElements"
          :create-new-scenario=true
          :available-elements="elementsList"
          :update-available-elements="updateElementList"
          :calling-api="callingApi"
          :selected-elements="computedElements"
          :show-validate="false"
          :allow-edit="false"
          :allow-delete="false"
          :mode="2"
          @update-elements="onElementListUpdated($event)"
          v-on="$listeners"
        />
      </v-col>
      <v-col cols="12">
        <h3 class="pa-3">{{ selectPreferedOptionLabel }}:</h3>
      </v-col>
      <v-col cols="12" v-if="selectedPref.id && conflictSelectedPref.id">
        <wigo-chip
          v-show="!validatedOption || validatedOption === selectedPref[wigoProperty]"
          :wigo="selectedPref[wigoProperty]"
          :outlined="selectedOption !== selectedPref[wigoProperty]"
          @click.native="selectOption(selectedPref[wigoProperty])"
          :asGroup="asGroup"
        />
        <wigo-chip
          v-show="!validatedOption || validatedOption === conflictSelectedPref[wigoProperty]"
          :wigo="conflictSelectedPref[wigoProperty]"
          :outlined="selectedOption !== conflictSelectedPref[wigoProperty]"
          @click.native="selectOption(conflictSelectedPref[wigoProperty])"
          :asGroup="asGroup"
        />
        <v-chip
          v-show="!validatedOption"
          @click="resetOptions"
          :outlined="!toggleNone"
          color="primary"
          link
          label
          :class="`px-6 pt-0 mr-1 my-1`"
        >
          {{ enableFinishButton && selectedPreferencesCombinationExtendable ? $t('conflict.iHaveFinished') : $t('global.nonSense') }}
        </v-chip>
      </v-col>
    </v-row>
    <v-row
      v-if="!validatedOption && (selectedOption || toggleNone)"
      dense
      justify="center"
    >
      <raison-button
        :button-label="$t('global.validate')"
        :button-function="!selectedPreferencesCombinationExtendable ? () => { warnClearDialog = true } : validateFavoriteOption"
        :loading="loading"
        :disabled="loading || (requiredAddNewElements && (!isElementAdded && !toggleNone) && selectedPreferencesCombinationExtendable)"
      >
        <template v-slot:append>
          <v-icon right>mdi-arrow-right</v-icon>
        </template>
      </raison-button>
    </v-row>

    <warn-clear-resolution-dialog
      :loading="loading"
      :open="warnClearDialog"
      @confirm="clearThenvalidateFavoriteOption"
      @cancel="warnClearDialog = false"
    />
  </div>
</template>

<script>
import RaisonButton from '@/components/global/RaisonButton';
import OptionBlock from '@/components/global/OptionBlock';
import InputScenarioElements from '@/components/global/InputScenarioElements';
import WarnClearResolutionDialog from '@/components/conflictResolution/WarnClearResolutionDialog';
import WigoChip from '@/components/global/WigoChip.vue';

import elementService from '@/services/elements.js';
import believeService from '@/services/believes.js';
import scenarioService from '@/services/scenarios.js';
import optionService from '@/services/options.js';
import {IsCohereService} from '@/services/isCohere.js'
import _ from 'lodash';

function sameId(o1, o2) {
  return  o1.id === o2.id;
}

export default {
  name: 'Option4Workflow',

  components: { RaisonButton, OptionBlock, InputScenarioElements, WigoChip, WarnClearResolutionDialog },

  data () {
    return {
      //all available elements
      elementsList: [],
      //element list including elements added in inputscenarioElement component
      updatedElementsList: [],
      //list of incompatibles
      incompatibleList: [],
      refreshInputScenarioElements: false,
      selectedOption: undefined,
      toggleNone: false,
      validatedOption: false,
      callingApi: false,
      selectedPref: {},
      conflictSelectedPref: {},
      projectId: '',
      conflictingOptions: [],
      inputScenarioElementsKey: 1,
      loading: false,
      preferencesCombinations: [],
      warnClearDialog: false,
    };
  },

  props: {
    availableOptions: {
      type: Array,
      required: true,
    },
    preferenceList: {
      type: Array,
      required: true,
    },
    workingPreferences: {
      type: Object,
      required: false,
      default: undefined
    },
    enableFinishButton: {
      type: Boolean,
      required: true,
    },
    forceAdd: {
      type: Boolean,
      required: false,
      default: false
    },
    reloadPreferencesList: {
      type: Function,
      required: true,
    },
    beliefMode: {
      type: Boolean,
      required: false,
      default: false,
    }
  },

  watch: {
    /*
     * In case of a backtracking we need to ensure the selected pref is 
     * the one related to the working context.
     */
    workingPreferences(currentValue, previousValue) {
      if(currentValue !== previousValue && this.workingPreferences.pref1 && this.workingPreferences.pref2) {
        this.selectedPref = this.workingPreferences.pref1;
        this.conflictSelectedPref = this.workingPreferences.pref2;
      }
    }
  },

  computed: {

    asGroup() {
      return !!this.selectedPref.groupId || !!this.conflictSelectedPref.groupId;
    },

    selectPreferedOptionLabel() {
      let label = 'conflict.selectFavoriteOption';
      if(this.asGroup) {
        label = 'conflict.selectFavoriteGroupOfOptions';
      }
      else if(this.beliefMode) {
        label = 'conflict.selectFavoriteBelief';
      }
      return this.$t(label);
    },

    addElementsLabel() {
      let label = 'conflict.inThisCombination';
      if(this.asGroup) {
        label = 'conflict.inThisCombinationForGroup'
      }
      if(this.beliefMode) {
        label = 'conflict.inThisCombinationForBeliefs'
      }
      if(this.requiredAddNewElements) {
        label = 'conflict.inThisCombinationAddElements'
        if(this.beliefMode) {
          label = 'conflict.inThisCombinationAddElementsForBeliefs'
        }
      }
      return this.$t(label);
    },

    optionProperty() {
      return believeService.optionProperty(this.beliefMode);
    },
    optioncomplementProperty() {
      return believeService.optioncomplementProperty(this.beliefMode);
    },
    wigoProperty() {
      return believeService.wigoProperty(this.beliefMode);
    },

    hasPreferencesCombinationSelected() {
      return this.workingPreferences.pref1 !== null;
    },

    lock() {
      return (
        (this.selectedPref.scenario.level > 1 || this.conflictSelectedPref.scenario.level > 1) // no minimal scenarios
        || this.enableFinishButton) // button finish enabled (at least one contextual preferences)
        && this.selectedPreferencesCombinationExtendable // current combination extendable
    },

    requiredAddNewElements: {
        get () {
            return this.forceAdd;
        },
        set (value) {
            this.$emit('update:forceAdd', value);
        }
    },

    computedPrefList() {
      let computedPreferenceList = JSON.parse(JSON.stringify(this.preferenceList))
      const result = computedPreferenceList
        .filter(pref => scenarioService.isMinimalPreference(pref, this.beliefMode))
        .filter(pref => pref[this.wigoProperty].length > 0 && this.hasConflict(pref[this.wigoProperty][0]))
        .filter(pref => this.hasPreferencesCombinationSelected || this.listNotIncompatibles(pref).length > 0)
        .map(pref => {
          pref.isAllConflictResolved = this.isAllConflictResolved(pref);
          return pref;
        })
        .sort((a,b) => {
          const isAResolved = a.isAllConflictResolved;
          const isBresolved = b.isAllConflictResolved;
          if (isAResolved > isBresolved) return 1
          if (isAResolved < isBresolved) return -1
          else return 0
        });
      return result;
    },

    computedConflictPrefList () { 
      return this.getConflictPrefList(this.selectedPref)
    },

    computedElements () { 
      if (!this.selectedPref.id || !this.conflictSelectedPref.id) return []
      let array = [...this.selectedPref.scenario.elements, ... this.conflictSelectedPref.scenario.elements];
      array = _.uniqWith(array, sameId);
      return array;
    },

    computedOtherOption () {
        if (this.selectedOption === this.selectedPref[this.wigoProperty]) {
          return this.conflictSelectedPref[this.wigoProperty];
        } else {
          return this.selectedPref[this.wigoProperty];
        }
    },

    isElementAdded () {
      return this.updatedElementsList.length > this.computedElements.length 
    },

    selectedPreferencesCombination() {
      // console.log('selectedPreferencesCombination', this.preferencesCombinations);
      return this.preferencesCombinations.find(
        combination => this.arePrefsInCombination(this.conflictSelectedPref, this.selectedPref, combination)
      );
    },

    selectedPreferencesCombinationExtendable () {
      const combination = this.selectedPreferencesCombination;
      if(combination) {
        return combination.extend;
      }
      return true;
    }

  },

  methods: {
    defaultSelectedBelief(beliefId) {
      const firstPreferenceWithBelief = this.preferenceList
        .filter(pref => pref.scenario.level === 1)
        .find(pref => pref.wigoBelief.find(belief => belief.id === beliefId));
      this.changeSelectedPref(firstPreferenceWithBelief, 1);
    },

    changeSelectedPref (pref, part) {
      if (part === 1) {
        this.selectedPref = pref;
        this.conflictSelectedPref = this.computedConflictPrefList[0];
      } else {
        this.conflictSelectedPref = pref;
      }
      this.$emit('piPrefAndPjPrefSelected', {piPref: this.selectedPref, pjPref: this.conflictSelectedPref});
      this.reloadInputScenarioElements()
    },

    onElementListUpdated (list) {
      this.updatedElementsList.splice(0, this.updatedElementsList.length, ...list);
    },

    resetOptions () {
      this.toggleNone = !this.toggleNone;
      if (this.toggleNone ) {
        this.selectedOption = undefined;
      }
      this.requiredAddNewElements = false;
    },

    selectOption (option) {
      if (this.validatedOption) return;
      if (this.selectedOption === option) {
        this.selectedOption = undefined;
      } else {
        this.toggleNone = false;
        this.selectedOption = option;
      }
    },

    clearCombinationResolution() {
      scenarioService.clearCombinationResolution(this.projectId, this.selectedPref.id, this.conflictSelectedPref.id)
        .then(() => {
          this.reloadPreferencesList()
            .then(() => {
              console.log('reloadPreferencesList done')
              const selectedCombinationId = this.selectedPreferencesCombination.id;
              this.preferencesCombinations.splice(this.preferenceList.findIndex(pref => pref.id === selectedCombinationId), 1);
              console.log('reload combinations',this.preferencesCombinations);
            })
            .catch(error => this.$emit('raison-event', { type: 'error', messages: [error] }))
            .finally(() => {
              this.warnClearDialog = false;
              this.loading = false;
            });
        })
        .catch(error => this.$emit('raison-event', { type: 'error', messages: [error] }))

    },

    clearThenvalidateFavoriteOption() {
      this.loading = true;
      scenarioService.clearCombinationResolution(this.projectId, this.selectedPref.id, this.conflictSelectedPref.id)
        .then(() => {
          scenarioService.getAllPreferences(this.$route.params.id).then(res => {
            this.$emit('refreshPreferences', res.data);
            this.preferencesCombinations.splice(this.preferencesCombinations.findIndex(combination => combination.id === this.selectedPreferencesCombination.id), 1);
            this.validateFavoriteOption();
          }).catch(err => {
            this.$emit('raison-event', { type: 'error', messages: [err] });
          }).finally(() => {
            this.warnClearDialog = false;
            this.loading = false;
          });
        })
        .catch(error => this.$emit('raison-event', { type: 'error', messages: [error] }))
        
    },

    validateNonSenseOrFinished() {
      console.log("validateNonSenseOrFinished", this.preferencesCombinations);

      this.loading = true;

      if (this.selectedPref.scenario.level === 1) {
        scenarioService.combinePreferencesResolved(this.projectId, this.selectedPref.id, this.conflictSelectedPref.id)
        .then((response) => {
          console.log("combinePreferencesResolved success", response.data)
          this.preferencesCombinations.push(response.data);
          console.log('preferencesCombinations', this.preferencesCombinations);
          console.log('validate non sens combinations', this.selectedPreferencesCombination);
          this.$emit('nextTodo');
        })
        .catch(error => {
          this.$emit('raison-event', { type: 'error', message: [error] });
        })
        .finally(() => {
          this.resetOptions();
          this.loading = false;
        })
      } else {
        this.reloadInputScenarioElements();
        this.$emit('nextTodo')
        this.loading = false;
        this.resetOptions();

      }
    },

    validateFavoriteOption () {
        this.loading = true;
        
        const nonSenseOrFinishedSelected = this.toggleNone && this.selectedOption === undefined;
        if (nonSenseOrFinishedSelected) {
          this.validateNonSenseOrFinished();
          return;
        }

        let originalScenario = this.selectedPref.scenario;
        const newScenario = {
          name: "Comes from case 6",
          elements: [...this.updatedElementsList],
          level: this.selectedPref.scenario.level + 1,
        };

          let preference = {
            name: 'Comes from case 6',
            scenario: newScenario,
            previousPreferences: [this.selectedPref.id, this.conflictSelectedPref.id],
          };
          if(!this.selectedPref?.groupId?.startsWith('$')) {
            preference.groupId = this.selectedPref.groupId;
          }
          if (this.beliefMode) {
            preference = {
              ...preference,
              wigoBelief: this.selectedOption.map(option => {return {id: option.id}}),
              logoBelief: this.computedOtherOption.map(option => {return {id: option.id}}),
            }
          } else {
            preference = {
              ...preference,
              wigo: this.selectedOption.map(option => {return {id: option.id}}),
              logo: this.computedOtherOption.map(option => {return {id: option.id}}),
            }
          }

          //create scenario preference
          scenarioService.createPreferences(this.$route.params.id, [preference])
            .then((prefResponse) => {
              const P = prefResponse.data[0]
              this.$emit('raison-event', {type: 'success', messages: [this.$t('scenario.created')]});
              this.$emit('newScenario', P.scenario);
              this.$emit('addPreferenceToList', P);

              const newS = P.scenario;
              const newSId = newS.id;

              if (!this.isElementsAdded()) {
                console.log('case6 - mode1')
                //this.$emit('preferedOption', P.wigo)
                //this.$emit('updateCurrentPreference', P)

                /*
                We MUST NOT go back to this current combinaison
                because we made a default choice.
                And thus, we don't need to ask again the user
                if he could add an element.
                */
                //this.$emit('addTodo', this.createEventGoBackToWf6());

                if(this.selectedPref.scenario.level === 1 && this.conflictSelectedPref.scenario.level === 1) {
                  this.$emit('addTodo', this.createEventGoBackToWf6());
                  this.$emit('addTodo', {
                    todo: 'resolveCase6',
                    projectId: this.projectId,
                    piPref: this.selectedPref.id,
                    pjPref: this.conflictSelectedPref.id
                  })
                }
                this.$emit('addTodo', {'preference': P, 'todo': 'f_4.1_04(P)'});
                this.$emit('nextTodo');
              } else {
                console.log('case6 - mode2');

                let whereToBackAfterCheckingCoherents = this.createEventGoBackToWf6();

                this.requiredAddNewElements=true;
                // For each Pn where scenario(Pn) = newS

                // let preferenceswithnewSc = this.preferenceList.filter(pref => pref.scenario.id === newSId);
                let preferenceswithnewSc = [ P ];
                let shouldTriggerNextTodo = false;
                IsCohereService.$on('addTodo', todo => {
                  this.$emit('addTodo', todo);
                  shouldTriggerNextTodo = true;
                })
                IsCohereService.searchCoherentScenarios(preferenceswithnewSc, this.preferenceList, originalScenario, newSId, whereToBackAfterCheckingCoherents, this.beliefMode);
                IsCohereService.$off('addTodo');

                this.selectedOption = undefined;
                //RTA: if i don't do that todo f_6_01(Pi,Pj) is triggered in next lines but state of the inputscenarioElement component doesn't
                // change so elements list in it remains the same as now (keep added elements)
                this.reloadInputScenarioElements();
                if (shouldTriggerNextTodo) {
                  this.$emit('nextTodo');
                }
              }
            })
            .catch(err => {
              console.log('createPreferences failed', err);
              this.$emit('raison-event', { type: 'error', messages: [err] });
            })
            .finally(() => {
              this.warnClearDialog = false;
              this.loading = false;
            });
        // })

    },

    // TODO quentin comment brancher cette redirection avec les nextTodo? 
    // Le composant pourrait se charger de sélectionner la première combinaison non résolu?
    selectNextConflict() {
      if (this.computedConflictPrefList.length > 1) {
        this.conflictSelectedPref = this.computedConflictPrefList.find(pref => pref.id !== this.conflictSelectedPref.id)
      } else {
        const next = this.computedPrefList.find(pref => pref.id !== this.selectedPref.id)
        this.selectedPref = next ? next : {}
      }
      this.selectedOption = undefined
      this.toggleNone = false
    },

    isElementsAdded () {
      const elementsIds = this.computedElements.map(e => e.id)
      return this.updatedElementsList.find(e => elementsIds.indexOf(e.id) === -1 ) !== undefined
    },
    
    emitEvent(eventType, eventName, eventContent) {
      this.$emit(eventType, eventName, eventContent);
    },

    updateElementList(elements) {
      this.elementsList = elements
    },

    hasConflict(option) {
      let optionId = option.id;
      let found = this.conflictingOptions.some(complement => 
        complement[this.optionProperty].id === optionId || complement[this.optioncomplementProperty].id === optionId
      );
      return found;
    },

    hasConflictWith(option, withOption) {
      let optionId = option.id;
      let withOptionId = withOption.id;
      let found = this.conflictingOptions
        .some(complement => 
             (complement[this.optionProperty].id === optionId && complement[this.optioncomplementProperty].id === withOptionId)
         ||  (complement[this.optionProperty].id === withOptionId && complement[this.optioncomplementProperty].id === optionId)
        );
      return found;
    },

    isIncompatible(scenario1, scenario2) {
      let isIncompatible = this.incompatibleList
        .some(incompatible =>
             (incompatible.scenario.id === scenario1.id && incompatible.scenarioincompatible.id === scenario2.id)
          || (incompatible.scenario.id === scenario2.id && incompatible.scenarioincompatible.id === scenario1.id)
        );
      return isIncompatible;
    },

    listNotIncompatibles(preference) {
      let list2 = this.preferenceList
        .filter(pref => scenarioService.isMinimalPreference(pref, this.beliefMode, true))
        .filter(pref => pref.scenario.id !== preference.scenario.id)
        // at level 1 we can have only one option in wigo
        .filter(pref => pref[this.wigoProperty].length > 0 && preference[this.wigoProperty].length > 0 && this.hasConflictWith(pref[this.wigoProperty][0], preference[this.wigoProperty][0]))
        .filter(pref => !this.isIncompatible(pref.scenario, preference.scenario));
      return list2;
    },

    reloadInputScenarioElements () {
      this.loading = false;
      this.inputScenarioElementsKey = this.inputScenarioElementsKey + 1
    },

    initComponent() {
      // console.log('initComponent');
      this.selectedOption = undefined;
      this.toggleNone = false;

      Promise.all([
        elementService.getElements(this.projectId),
        scenarioService.getIncompatibleScenarios(this.projectId),
        this.beliefMode ? believeService.getAllConflictingBelieves(this.projectId) : optionService.getAllConflictingOptions(this.projectId),
        scenarioService.getAllPreferencesCombinations(this.projectId),
      ])
      .then(responses => {
        this.elementsList.splice(0, this.elementsList.length, ...responses[0].data);
        this.incompatibleList.splice(0, this.incompatibleList.length, ...responses[1].data);
        this.conflictingOptions.splice(0, this.conflictingOptions.length, ...responses[2].data);
        // console.log('conflictingOptions', this.conflictingOptions);
        this.preferencesCombinations.splice(0, this.preferencesCombinations.length, ...responses[3].data);
        if (!this.workingPreferences.pref1) {
          if(this.$route.params.beliefId) {
            this.defaultSelectedBelief(this.$route.params.beliefId);
          }
          else {
            this.selectedPref = this.computedPrefList[0];
            this.conflictSelectedPref = this.computedConflictPrefList[0];
            this.$emit('piPrefAndPjPrefSelected', {piPref: this.selectedPref, pjPref: this.conflictSelectedPref});
          }
        }
        else {
          // console.log("initComponent case2")
          this.selectedPref = this.workingPreferences.pref1;
          this.conflictSelectedPref = this.workingPreferences.pref2;
        }
      })
      .catch(err => {
        console.log(err);
        this.$emit('raison-event', { type: 'error', messages: [err] });
      })
      .finally(() => {
        this.callingApi = false;
      });

    },

    createEventGoBackToWf6 () {
      let todo = {
        todo: 'f_6_01(Pi,Pj)', 
        workingPreferences: {
          pref1: { ...this.selectedPref },
          pref2: { ...this.conflictSelectedPref },
        }
      };
      if (this.isElementAdded) {
        todo = {
          ...todo,
          forceAdd: true,
        };
      } 
      return todo;
    },

    isConflictResolved (piPref, pjPref) {
      let result = this.preferencesCombinations.some(
        combination => this.arePrefsInCombination(piPref, pjPref, combination) && !combination.extend
      );
      // console.log('isConflictResolved', piPref.id, pjPref.id, result)
      return result;
    },

    isAllConflictResolved (preference) {
      const conflictingPrefs = this.getConflictPrefList(preference)
      let result = conflictingPrefs.length === 0 || conflictingPrefs.filter(pref => pref.isCombinationResolved).length === conflictingPrefs.length
      // console.log('isAllConflictResolved',preference.id,conflictingPrefs.map(pref => pref.id + '/' + pref.isCombinationResolved), result)
      return result;
    },

    getConflictPrefList (pref) { 
      if (!pref) {
        return [];
      }
      const incompatiblesPreferences = this.listNotIncompatibles(pref)
      let conflictingPreferencesWithPref = JSON.parse(JSON.stringify(incompatiblesPreferences))
      return conflictingPreferencesWithPref
        .map(prefj => {
          prefj.isCombinationResolved = this.isConflictResolved(pref, prefj);
          return prefj;
        })
        .sort((a,b) => this.comparePreferencesByCombinationResolvedThenById(a, b));
    },

    comparePreferencesByCombinationResolved(preference1, preference2) {
      if (preference2.isCombinationResolved && !preference1.isCombinationResolved) return -1
      if (preference1.isCombinationResolved && !preference2.isCombinationResolved) return 1
      return 0
    },

    comparePreferencesById(preference1, preference2) {
      return preference1.id.localeCompare(preference2.id)
    },

    comparePreferencesByCombinationResolvedThenById(preference1, preference2) {
      const sort = this.comparePreferencesByCombinationResolved(preference1, preference2)
      if(sort !== 0)
        return sort
      return this.comparePreferencesById(preference1, preference2)
    },

    arePrefsInCombination (preference1, preference2, combination) {
      return (combination.preference.id === preference1.id && combination.combinedPreference.id === preference2.id)
      || (combination.preference.id === preference2.id && combination.combinedPreference.id === preference1.id)
    }
  },

  created () {
    this.projectId = this.$route.params.id;
    this.callingApi = true;
    this.initComponent();
  },

};
</script>
